import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

const REFUND_CONFIRMATION = 'REFUND';

const MedicineRefundModal = ({ mounted, show, onClose, onSubmit, submitting, error }) => {
  const [refundReason, setRefundReason] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (show) {
      setRefundReason('');
      setConfirmationText('');
      setFieldErrors({});
    }
  }, [show]);

  useEffect(() => {
    if (error && typeof error === 'object' && !Array.isArray(error)) {
      setFieldErrors(error);
    } else if (error) {
      setFieldErrors({ _message: typeof error === 'string' ? error : 'Something went wrong.' });
    } else {
      setFieldErrors({});
    }
  }, [error]);

  if (!mounted || !show) return null;

  const isConfirmValid = confirmationText === REFUND_CONFIRMATION;
  const isReasonValid = refundReason.trim().length > 0;
  const canSubmit = isReasonValid && isConfirmValid && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      refund_reason: refundReason.trim(),
      confirmation_text: REFUND_CONFIRMATION
    });
  };

  return createPortal(
    <div className="fixed top-0 left-0 w-screen h-screen bg-black/80 z-[9999] m-0 p-0 flex items-center justify-center">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-xl shadow-xl m-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="font-semibold">Refund Medicine Order</div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600 bg-slate-50 border border-slate-200 rounded-md p-3">
            This will mark the order as refunded, reverse cashback into the subscriber&apos;s wallet, and create a refund ledger entry. This action cannot be undone.
          </div>

          {fieldErrors._message && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              {fieldErrors._message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refund reason <span className="text-red-500">*</span>
            </label>
            <textarea
              className={`w-full border rounded-md px-3 py-2 min-h-[80px] ${
                fieldErrors.refund_reason ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter reason for refund"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              disabled={submitting}
            />
            {fieldErrors.refund_reason && Array.isArray(fieldErrors.refund_reason) && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.refund_reason[0]}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type REFUND to confirm <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`w-full border rounded-md px-3 py-2 font-mono ${
                fieldErrors.confirmation_text ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="REFUND"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-gray-500">Must be exactly &quot;REFUND&quot; (case-sensitive).</p>
            {fieldErrors.confirmation_text && Array.isArray(fieldErrors.confirmation_text) && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.confirmation_text[0]}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                canSubmit ? 'bg-slate-600 hover:bg-slate-700' : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {submitting ? 'Refunding...' : 'Refund order'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MedicineRefundModal;
